import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { formatAuditTimestamp } from "@/lib/auditFormat";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { aclRuleForPath, isAllowed } from "@/lib/acl";
import { useCurrentUser } from "@/lib/auth";
import { isAppAdmin } from "@/lib/permissions";
import {
  TICKET_CATEGORIES,
  TICKET_CATEGORY_LABELS,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  categoryLabel,
  formatTicketNumber,
  priorityLabel,
  statusLabel,
  ticketUserLogin,
  ticketUserName,
} from "@/lib/supportTickets";
import { usePageHeader } from "@/layout/usePageHeader";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { TicketMessageImages } from "@/pages/tickets/TicketMessageImages";
import {
  TicketCategoryBadge,
  TicketPriorityBadge,
  TicketStatusBadge,
} from "@/pages/tickets/ticketUi";
import type {
  TicketCategory,
  TicketDetail,
  TicketPriority,
  TicketStatus,
  TicketUpdateBody,
} from "@/types/supportTicket";

interface Draft {
  title: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  assigneeLogin: string;
  assigneeName: string;
  locationHint: string;
  dueOn: string;
}

function draftFrom(ticket: TicketDetail): Draft {
  return {
    title: ticket.title,
    description: ticket.description,
    category: ticket.category,
    priority: ticket.priority,
    status: ticket.status,
    assigneeLogin: ticket.assignee_login ?? "",
    assigneeName: ticket.assignee_name ?? "",
    locationHint: ticket.location_hint ?? "",
    dueOn: ticket.due_on ?? "",
  };
}

export function TicketDetailPage() {
  const { ticketId = "" } = useParams();
  const { setPageMeta } = usePageHeader();
  const { user, loading: userLoading } = useCurrentUser();
  const groups = user?.groups ?? [];
  const allowed = isAllowed(groups, aclRuleForPath("/tickets"));

  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const [editingContent, setEditingContent] = useState(false);

  useEffect(() => {
    if (!ticketId || !allowed) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<TicketDetail>(`/api/v1/tickets/${ticketId}`)
      .then((ticket) => {
        if (cancelled) return;
        setDetail(ticket);
        setDraft(draftFrom(ticket));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetail(null);
        setError(err instanceof Error ? err.message : "Ticket konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, ticketId]);

  useEffect(() => {
    setPageMeta({
      title: detail ? `${formatTicketNumber(detail.number)} ${detail.title}` : "Ticket",
      subtitle: detail
        ? `${statusLabel(detail.status)} · ${detail.requester_name}`
        : "Meldung",
    });
    return () => setPageMeta(null);
  }, [detail, setPageMeta]);

  async function applyUpdate(body: TicketUpdateBody) {
    if (!detail) return;
    setActing(true);
    setError(null);
    try {
      const next = await api.patch<TicketDetail>(`/api/v1/tickets/${detail.id}`, body);
      setDetail(next);
      setDraft(draftFrom(next));
      setEditingContent(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Änderung konnte nicht gespeichert werden.");
    } finally {
      setActing(false);
    }
  }

  async function submitComment(e: FormEvent) {
    e.preventDefault();
    if (!detail || !comment.trim()) return;
    setActing(true);
    setError(null);
    try {
      const next = await api.post<TicketDetail>(`/api/v1/tickets/${detail.id}/comments`, {
        body: comment.trim(),
        is_internal: detail.can_process && internal,
      });
      setDetail(next);
      setDraft(draftFrom(next));
      setComment("");
      setInternal(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Kommentar konnte nicht gespeichert werden.");
    } finally {
      setActing(false);
    }
  }

  if (userLoading || !user) {
    return <p className="text-sm text-label-secondary">Sitzung wird geprüft …</p>;
  }

  if (!allowed) {
    return (
      <Alert variant="error" title="Keine Berechtigung">
        Tickets sind für Ihre Rolle nicht freigegeben.
      </Alert>
    );
  }

  const myLogin = ticketUserLogin(user);
  const myName = ticketUserName(user);
  const showAdmin = detail?.can_process ?? isAppAdmin(groups);

  return (
    <div className="space-y-6">
      <Link to="/tickets" className="text-sm font-medium text-accent hover:underline">
        Alle Tickets
      </Link>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {loading ? <p className="text-sm text-label-secondary">Ticket wird geladen …</p> : null}

      {detail && draft ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="order-2 space-y-6 lg:order-1">
            <section className="rounded-xl border border-separator bg-card p-4">
              <div className="mb-3 flex flex-wrap gap-2">
                <TicketStatusBadge status={detail.status} />
                <TicketPriorityBadge priority={detail.priority} />
                <TicketCategoryBadge category={detail.category} />
              </div>
              <p className="whitespace-pre-wrap text-sm text-label">{detail.description}</p>
              {detail.location_hint ? (
                <p className="mt-3 text-sm text-label-secondary">Bezug: {detail.location_hint}</p>
              ) : null}
              {detail.can_edit ? (
                <div className="mt-4">
                  {editingContent ? (
                    <form
                      className="space-y-3"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void applyUpdate({
                          title: draft.title.trim(),
                          description: draft.description.trim(),
                          category: draft.category,
                          priority: draft.priority,
                          location_hint: draft.locationHint.trim() || null,
                        });
                      }}
                    >
                      <label className="block text-sm">
                        <span className="mb-1 block text-label-secondary">Titel</span>
                        <input
                          className="eh-input w-full"
                          value={draft.title}
                          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                          maxLength={200}
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="mb-1 block text-label-secondary">Beschreibung</span>
                        <textarea
                          className="eh-input min-h-28 w-full resize-y"
                          value={draft.description}
                          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                          maxLength={8000}
                        />
                      </label>
                      {!showAdmin ? (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <label className="block text-sm">
                            <span className="mb-1 block text-label-secondary">Kategorie</span>
                            <select
                              className="eh-input w-full"
                              value={draft.category}
                              onChange={(e) =>
                                setDraft({ ...draft, category: e.target.value as TicketCategory })
                              }
                            >
                              {TICKET_CATEGORIES.map((value) => (
                                <option key={value} value={value}>
                                  {TICKET_CATEGORY_LABELS[value]}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="block text-sm">
                            <span className="mb-1 block text-label-secondary">Priorität</span>
                            <select
                              className="eh-input w-full"
                              value={draft.priority}
                              onChange={(e) =>
                                setDraft({ ...draft, priority: e.target.value as TicketPriority })
                              }
                            >
                              {TICKET_PRIORITIES.map((value) => (
                                <option key={value} value={value}>
                                  {TICKET_PRIORITY_LABELS[value]}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      ) : null}
                      <label className="block text-sm">
                        <span className="mb-1 block text-label-secondary">Bezug</span>
                        <input
                          className="eh-input w-full"
                          value={draft.locationHint}
                          onChange={(e) => setDraft({ ...draft, locationHint: e.target.value })}
                          maxLength={200}
                        />
                      </label>
                      <div className="flex gap-2">
                        <Button type="submit" loading={acting}>
                          Angaben speichern
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            setDraft(draftFrom(detail));
                            setEditingContent(false);
                          }}
                        >
                          Abbrechen
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <Button type="button" variant="secondary" onClick={() => setEditingContent(true)}>
                      Angaben ändern
                    </Button>
                  )}
                </div>
              ) : null}
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-label">Verlauf</h2>
              {detail.messages.length === 0 ? (
                <p className="text-sm text-label-secondary">Noch keine Einträge.</p>
              ) : (
                <ol className="space-y-3">
                  {detail.messages.map((message) =>
                    message.kind === "event" ? (
                      <li key={message.id} className="text-center text-xs text-label-tertiary">
                        {message.body} · {message.author_name} · {formatAuditTimestamp(message.created_at)}
                      </li>
                    ) : (
                      <li
                        key={message.id}
                        className={clsx(
                          "rounded-xl border p-3",
                          message.is_internal
                            ? "border-dashed border-status-orange/50 bg-status-orange/5"
                            : "border-separator bg-card",
                        )}
                      >
                        <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-label-secondary">
                          <span className="font-medium text-label">{message.author_name}</span>
                          <span>{formatAuditTimestamp(message.created_at)}</span>
                          {message.is_internal ? (
                            <span className="rounded-full bg-status-orange/15 px-2 py-0.5 font-medium text-status-orange">
                              Interne Notiz
                            </span>
                          ) : null}
                        </div>
                        {message.body?.trim() ? (
                          <p className="whitespace-pre-wrap text-sm text-label">{message.body}</p>
                        ) : null}
                        <TicketMessageImages attachments={message.attachments ?? []} />
                      </li>
                    ),
                  )}
                </ol>
              )}
            </section>

            {detail.can_comment ? (
              <form className="space-y-3 rounded-xl border border-separator bg-card p-4" onSubmit={submitComment}>
                <label className="block text-sm">
                  <span className="mb-1 block text-label-secondary">Kommentar</span>
                  <textarea
                    className="eh-input min-h-24 w-full resize-y"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    maxLength={4000}
                    placeholder={
                      showAdmin ? "Antwort oder interne Notiz für Camping-Admin" : "Ergänzung zur Meldung"
                    }
                  />
                </label>
                {showAdmin ? (
                  <label className="flex items-center gap-2 text-sm text-label">
                    <input
                      type="checkbox"
                      checked={internal}
                      onChange={(e) => setInternal(e.target.checked)}
                    />
                    Interne Notiz, nur für Camping-Admin sichtbar
                  </label>
                ) : null}
                <Button type="submit" disabled={!comment.trim()} loading={acting}>
                  Kommentar speichern
                </Button>
              </form>
            ) : null}
          </div>

          <aside className="order-1 space-y-4 lg:order-2">
            <section className="rounded-xl border border-separator bg-card p-4 text-sm">
              <dl className="space-y-2">
                <div className="flex justify-between gap-3">
                  <dt className="text-label-secondary">Gemeldet von</dt>
                  <dd className="text-right">{detail.requester_name}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-label-secondary">Angelegt</dt>
                  <dd>{formatAuditTimestamp(detail.created_at)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-label-secondary">Aktualisiert</dt>
                  <dd>{formatAuditTimestamp(detail.updated_at)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-label-secondary">Zugewiesen</dt>
                  <dd className="text-right">{detail.assignee_name || "—"}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-label-secondary">Fällig</dt>
                  <dd>{detail.due_on ? formatIsoDateShort(detail.due_on) : "—"}</dd>
                </div>
              </dl>
            </section>

            {showAdmin ? (
              <section className="space-y-3 rounded-xl border border-separator bg-card p-4">
                <h2 className="text-sm font-semibold text-label">Bearbeiten</h2>
                <p className="text-xs text-label-secondary">
                  Status, Zuweisung und Abschluss sind der Gruppe Camping-Admin vorbehalten.
                </p>
                <div className="flex flex-wrap gap-2">
                  {detail.assignee_login !== myLogin || detail.status !== "in_progress" ? (
                    <Button
                      type="button"
                      size="sm"
                      disabled={acting}
                      onClick={() =>
                        void applyUpdate({
                          status: detail.allowed_statuses.includes("in_progress")
                            ? "in_progress"
                            : detail.status,
                          assignee_login: myLogin,
                          assignee_name: myName,
                        })
                      }
                    >
                      Übernehmen
                    </Button>
                  ) : null}
                  {detail.allowed_statuses.includes("resolved") && detail.status !== "resolved" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={acting}
                      onClick={() => void applyUpdate({ status: "resolved" })}
                    >
                      Erledigen
                    </Button>
                  ) : null}
                  {detail.allowed_statuses.includes("closed") && detail.status !== "closed" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={acting}
                      onClick={() => void applyUpdate({ status: "closed" })}
                    >
                      Schließen
                    </Button>
                  ) : null}
                  {detail.status !== "open" && detail.allowed_statuses.includes("open") ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={acting}
                      onClick={() => void applyUpdate({ status: "open" })}
                    >
                      Wieder öffnen
                    </Button>
                  ) : null}
                </div>

                <form
                  className="space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void applyUpdate({
                      status: draft.status,
                      priority: draft.priority,
                      category: draft.category,
                      assignee_login: draft.assigneeLogin.trim() || null,
                      assignee_name: draft.assigneeName.trim() || null,
                      due_on: draft.dueOn || null,
                      location_hint: draft.locationHint.trim() || null,
                    });
                  }}
                >
                  <label className="block text-sm">
                    <span className="mb-1 block text-label-secondary">Status</span>
                    <select
                      className="eh-input w-full"
                      value={draft.status}
                      onChange={(e) => setDraft({ ...draft, status: e.target.value as TicketStatus })}
                    >
                      {detail.allowed_statuses.map((value) => (
                        <option key={value} value={value}>
                          {statusLabel(value)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-label-secondary">Priorität</span>
                    <select
                      className="eh-input w-full"
                      value={draft.priority}
                      onChange={(e) =>
                        setDraft({ ...draft, priority: e.target.value as TicketPriority })
                      }
                    >
                      {TICKET_PRIORITIES.map((value) => (
                        <option key={value} value={value}>
                          {priorityLabel(value)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-label-secondary">Kategorie</span>
                    <select
                      className="eh-input w-full"
                      value={draft.category}
                      onChange={(e) =>
                        setDraft({ ...draft, category: e.target.value as TicketCategory })
                      }
                    >
                      {TICKET_CATEGORIES.map((value) => (
                        <option key={value} value={value}>
                          {categoryLabel(value)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-label-secondary">Zugewiesen an (Login)</span>
                    <input
                      className="eh-input w-full"
                      list="ticket-assignees"
                      value={draft.assigneeLogin}
                      onChange={(e) => setDraft({ ...draft, assigneeLogin: e.target.value })}
                      placeholder="leer lassen hebt die Zuweisung auf"
                    />
                    <datalist id="ticket-assignees">
                      <option value={myLogin}>{myName}</option>
                      {detail.assignees.map((person) => (
                        <option key={person.login} value={person.login}>
                          {person.name}
                        </option>
                      ))}
                    </datalist>
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-label-secondary">Name der Zuweisung</span>
                    <input
                      className="eh-input w-full"
                      value={draft.assigneeName}
                      onChange={(e) => setDraft({ ...draft, assigneeName: e.target.value })}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-label-secondary">Fällig am</span>
                    <input
                      className="eh-input w-full"
                      type="date"
                      value={draft.dueOn}
                      onChange={(e) => setDraft({ ...draft, dueOn: e.target.value })}
                    />
                  </label>
                  <Button type="submit" variant="secondary" loading={acting}>
                    Bearbeitung speichern
                  </Button>
                </form>
              </section>
            ) : (
              <p className="text-sm text-label-secondary">
                Sobald jemand aus der Gruppe Camping-Admin das Ticket übernimmt, sehen Sie den Status hier.
              </p>
            )}
          </aside>
        </div>
      ) : null}
    </div>
  );
}
